/**
 * A brand URL is authored text, and it is rendered to people who have not
 * logged in yet.
 *
 * `companyUrl` comes from `tenant_settings.brand_overrides`, typed into a
 * plain text field in the White-Label Editor, and the login page hangs it on
 * the tenant logo as an `href`. An `href` is not inert: `javascript:…` runs
 * on click, and `data:text/html,…` navigates to attacker-authored markup on
 * the same tab. Both would execute in the context of the login page, before
 * any authentication, for every visitor of that tenant.
 *
 * So a brand URL is allowed to be one of three things — an ordinary web link,
 * a mail link, or nothing at all. Anything else resolves to `undefined`, and
 * callers render plain content instead of a link. Relative paths are allowed
 * because they cannot carry a scheme.
 */

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

export function safeBrandUrl(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim();
  if (!value) return undefined;

  // A relative path or fragment carries no scheme, so it cannot be
  // javascript: or data: and is safe to pass through untouched.
  if (value.startsWith('/') || value.startsWith('#')) return value;

  let parsed: URL;
  try {
    // A base is required so a scheme-less value ("brijlabs.ai") parses at
    // all; it is only used to read the protocol back out.
    parsed = new URL(value, window.location.origin);
  } catch {
    return undefined;
  }

  // A scheme-less value inherits the page's own protocol from the base,
  // which is https in every deployed environment, so it passes — the point
  // of the check is to reject a value that named a dangerous scheme itself.
  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) return undefined;

  return value;
}
